import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

const SENTINEL_HEIGHT = 10

type Args = {
  targetRef?: RefObject<HTMLElement | undefined | null>
  rootRef?: RefObject<HTMLElement | undefined | null>
  onIntersect: () => void
  enabled?: boolean
  rootMargin?: string
  threshold?: number | number[]
}

export const useIntersectionObserver = ({
  targetRef,
  rootRef,
  onIntersect,
  enabled = true,
  rootMargin,
  threshold
}: Args) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const onIntersectRef = useRef(onIntersect)
  useEffect(() => {
    onIntersectRef.current = onIntersect
  })

  useEffect(() => {
    const target = targetRef?.current ?? sentinelRef.current
    if (!enabled || !target) return

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) onIntersectRef.current()
        }),
      { root: rootRef?.current ?? null, rootMargin, threshold }
    )

    observer.observe(target)

    return () => observer.unobserve(target)
  }, [enabled, rootMargin, threshold, targetRef, rootRef])

  return {
    observerElement: (
      <div ref={sentinelRef} style={{ height: SENTINEL_HEIGHT }} />
    )
  }
}
